import React, {useContext, useEffect, useState} from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {Box, Grid, Link, Typography} from "@material-ui/core";
import NoIndexDialog from "../noIndex/NoIndexDialog";
import DialogContent from "@material-ui/core/DialogContent";
import {isMobile, useI18N} from "../../../common/common";
import {NEWSLETTER_DIALOG_SHOWN} from "../../../common/constant";
import IconButton from "@material-ui/core/IconButton";
import {CustomButton, CloseIcon} from "@hapinest/theme";
import {useUserAccounts} from "../../../hooks/useUserAccounts";
import {useLocalStorage} from "../../../hooks/useLocalStorage";
import {UserContext} from "../../../store/providers/UserProvider";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import {IMPRINT, PRIVACY_POLICY, PROFILE_ACCOUNT_SETTINGS_PRIVACY_AND_NOTIFICATIONS} from "../../../common/routes";
import {checkShowNewsletterSignupV3} from "../../../common/configcat/functions";
import {useBreakpoints, useKeycloakFunctions} from "../../../hooks";
import {withSnackbar} from "../../hoc/SnackbarHOC";
import PropTypes from "prop-types";

const useStyles = makeStyles(theme => ({
    wrapper: {
        textAlign: "center",
        padding: theme.spacing(3)
    },
}));

const NewsletterDialog = ({showSnackbar}) => {
    const isMobileDevice = isMobile();
    const classes = useStyles();
    const {i18n} = useI18N();
    const {sendCustomEvent, eventNames} = useAnalytics();
    const {
        updateUserSettings,
        updatedUserSettings,
        loadingUpdateUserSettings,
        errorUpdateUserSettings
    } = useUserAccounts();
    const {tokenParsed} = useKeycloakFunctions();
    const {isExtraSmallScreen} = useBreakpoints();

    const {userDispatch} = useContext(UserContext)[1];
    const setNewsletterDialogShown = useLocalStorage(NEWSLETTER_DIALOG_SHOWN)[1];
    const [dialogOpen, setDialogOpen] = useState(true);
    const [answer, setAnswer] = useState(undefined);

    // Newsletter alternate variant
    const [isNewsletterDialogAlternateVariant, setNewsletterDialogAlternateVariant] = useState();
    const [abTestData, setAbTestData] = useState({});

    useEffect(() => {
        checkShowNewsletterSignupV3(tokenParsed?.preferred_username).then((value) => {
            setNewsletterDialogAlternateVariant(value);
        });

        // This is when we do not have A/B testing. So commenting out the below code for now.
        // sendCustomEvent({
        //     event_name: eventNames.NEWSLETTER_PROMPT_VIEW
        // });
    }, []);

    useEffect(() => {
        if (isNewsletterDialogAlternateVariant !== undefined) {
            const data = {
                abtest_id: "HE_showNewsletterSignupV3_20240209",
                abtest_name: "Show Newsletter V3",
                abtest_variation_id: isNewsletterDialogAlternateVariant ? "variant2" : "variant1",
                abtest_variation_name: isNewsletterDialogAlternateVariant ? "Variant 2" : "Variant 1"
            };

            setAbTestData(data);

            sendCustomEvent({
                event_name: eventNames.NEWSLETTER_PROMPT_VIEW,
                ...data
            });
        }
    }, [isNewsletterDialogAlternateVariant]);

    useEffect(() => {
        if (userDispatch) {
            userDispatch({type: "UPDATE_USER_SETTINGS", payload: {
                userSettings: updatedUserSettings,
                loadingUserSettings: loadingUpdateUserSettings,
                errorUserSettings: errorUpdateUserSettings
            }});
        }
    }, [updatedUserSettings, loadingUpdateUserSettings, errorUpdateUserSettings]);

    useEffect(() => {
        if (answer !== undefined && updatedUserSettings) {
            sendCustomEvent({
                event_name: eventNames.NEWSLETTER_SUBSCRIBE,
                ...abTestData
            });

            showSnackbar({
                type: "success",
                withIcon: true,
                message: i18n("dialog.newsletter.subscription_success")
            });
        }
    }, [updatedUserSettings, answer]);

    const subscribeToNewsletter = (answered) => {
        setAnswer(answered);
        setDialogOpen(false);
        updateUserSettings({offers_by_email: answered});
    };

    const handleCloseDialog = () => {
        sendCustomEvent({
            event_name: eventNames.NEWSLETTER_CLOSE,
            ...abTestData
        });

        setDialogOpen(false);
        setNewsletterDialogShown(NEWSLETTER_DIALOG_SHOWN, true);
    };

    const getFootnoteHyperlink = (str, uri) => {
        return (
            <Link
                key={str}
                href={uri}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setNewsletterDialogShown(NEWSLETTER_DIALOG_SHOWN, true)}
            >
                <Typography
                    variant={"caption"}
                >
                    {str}
                </Typography>
            </Link>
        );
    };

    return (
        <NoIndexDialog
            smallerZIndex
            onBackdropClick={() => {handleCloseDialog();}}
            open={dialogOpen}
            maxWidth={"sm"}
            fullScreen={isMobileDevice}
        >
            <DialogContent
                classes={{root: classes.wrapper}}
            >
                <Grid
                    container
                    alignItems="center"
                    spacing={0}
                    justify="center"
                >
                    <Grid
                        item
                        xs={12}
                        align={"right"}
                    >
                        <IconButton
                            aria-label="close"
                            onClick={() => {handleCloseDialog();}}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Grid>
                    <Grid
                        item
                        align={"center"}
                    >
                        <Box
                            width={100}
                            mb={4}
                        >
                            <img
                                src={isNewsletterDialogAlternateVariant ? "/images/newsletter-optin.svg" : "/images/request-received-icon.svg"}
                                alt="Subscribe to our newsletter"
                            />
                        </Box>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <Typography
                            variant="h3"
                        >
                            <Box
                                mb={2}
                            >
                                {isNewsletterDialogAlternateVariant ? i18n("dialog.newsletter.heading") : i18n("dialog.newsletter.title")}
                            </Box>
                        </Typography>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <Box
                            mb={4}
                        >
                            <Typography
                                variant="body1"
                            >
                                {isNewsletterDialogAlternateVariant ? i18n("dialog.newsletter.body") : i18n("dialog.newsletter.description")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <Box
                            mb={2}
                        >
                            <CustomButton
                                variant={"contained"}
                                disableElevation
                                onClick={() => {subscribeToNewsletter(true);}}
                                fullWidth={isExtraSmallScreen()}
                            >
                                {isNewsletterDialogAlternateVariant ? i18n("dialog.newsletter.accept") : i18n("dialog.newsletter.cta_accept")}
                            </CustomButton>
                        </Box>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <CustomButton
                            className={classes.declineBtn}
                            color="black"
                            disableElevation
                            onClick={() => {subscribeToNewsletter(false);}}
                            fullWidth={isExtraSmallScreen()}
                        >
                            {isNewsletterDialogAlternateVariant ? i18n("dialog.newsletter.decline") : i18n("dialog.newsletter.cta_decline")}
                        </CustomButton>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <Box
                            mt={4}
                        >
                            <Typography
                                variant="caption"
                            >
                                {i18n(isNewsletterDialogAlternateVariant ? "dialog.newsletter.caption" : "dialog.newsletter.footnote", {
                                    hyperlink1: (str) => getFootnoteHyperlink(str, PROFILE_ACCOUNT_SETTINGS_PRIVACY_AND_NOTIFICATIONS),
                                    hyperlink2: (str) => getFootnoteHyperlink(str, IMPRINT + PRIVACY_POLICY)
                                })}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
        </NoIndexDialog>
    );
};

NewsletterDialog.propTypes = {
    showSnackbar: PropTypes.func,
};

export default withSnackbar(NewsletterDialog);
